import _ from 'lodash';
import { IObservable, Subject } from '~/rage/utils';
import {
	ITechTuningItem,
	TechTuningCaseType,
	TechTuningCategory,
	TechTuningRarity,
	WalletType,
} from '~/shared';
import {
	ITechTuningService,
	TechTuningCaseConfigByType,
	VehicleTechTuningItemConfig,
} from './techTuningServiceTypes';

export class TechTuningServiceStub implements ITechTuningService {
	private readonly ITEMS_STUB = [
		{
			id: '1',
			name: 'Item 1',
			model: 'IFN',
			price: 90,
			category: TechTuningCategory.ENGINE,
			rarity: TechTuningRarity.LEGENDARY,
			discount: 0.1,
			discountTimestampStart: Date.now() - 1000,
			discountTimestampEnd: Date.now() - 1000,
			properties: {
				boost: 10,
				acceleration: 10,
				traction: null,
				braking: null,
			},
			isShopItem: false,
		},
		{
			id: '2',
			name: 'Item 2',
			model: 'IFN',
			price: 100,
			category: TechTuningCategory.ENGINE,
			rarity: TechTuningRarity.RARE,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 15,
				acceleration: 11,
				traction: null,
				braking: 10,
			},
			isShopItem: true,
		},
		{
			id: '23',
			name: 'Item 23',
			model: 'IFN',
			price: 100,
			category: TechTuningCategory.ENGINE,
			rarity: TechTuningRarity.RARE,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 14,
				acceleration: 9,
				traction: null,
				braking: 10,
			},
			isShopItem: true,
		},
		{
			id: '24',
			name: 'Item 24',
			model: 'IFN',
			price: 100,
			category: TechTuningCategory.ENGINE,
			rarity: TechTuningRarity.RARE,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 10,
				acceleration: null,
				traction: null,
				braking: 10,
			},
			isShopItem: true,
		},
		{
			id: '25',
			name: 'Item 25',
			model: 'IFN',
			price: 100,
			category: TechTuningCategory.ENGINE,
			rarity: TechTuningRarity.RARE,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 10,
				acceleration: null,
				traction: null,
				braking: 10,
			},
			isShopItem: true,
		},
		{
			id: '3',
			name: 'Item 3',
			model: 'IFN',
			price: 110,
			category: TechTuningCategory.TURBO,
			rarity: TechTuningRarity.EPIC,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 12,
				acceleration: null,
				traction: null,
				braking: null,
			},
			isShopItem: true,
		},
		{
			id: '4',
			name: 'Item 4',
			model: 'IFN',
			price: 120,
			category: TechTuningCategory.EXHAUST,
			rarity: TechTuningRarity.RARE,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 11,
				acceleration: null,
				traction: null,
				braking: 10,
			},
			isShopItem: true,
		},
		{
			id: '5',
			name: 'Item 5',
			model: 'IFN',
			price: 130,
			category: TechTuningCategory.RADIATOR,
			rarity: TechTuningRarity.EPIC,
			discount: 0.2,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 5,
				acceleration: 10,
				traction: null,
				braking: null,
			},
			isShopItem: true,
		},
		{
			id: '6',
			name: 'Item 6',
			model: 'IFN',
			price: 140,
			category: TechTuningCategory.STARTER,
			rarity: TechTuningRarity.RARE,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			properties: {
				boost: 3,
				acceleration: null,
				traction: null,
				braking: null,
			},
			isShopItem: true,
		},
	];

	private readonly CASES_STUB: TechTuningCaseConfigByType = {
		[TechTuningCaseType.STANDART]: {
			type: TechTuningCaseType.STANDART,
			price: 100,
			walletType: WalletType.Bank,
			discount: 0.1,
			discountTimestampStart: Date.now() - 10_000,
			discountTimestampEnd: Date.now() + 10_000,
			itemsRarity: [TechTuningRarity.COMMON, TechTuningRarity.UNCOMMON],
			inStock: 0,
		},
		[TechTuningCaseType.DIAMOND]: {
			type: TechTuningCaseType.DIAMOND,
			price: 200,
			walletType: WalletType.Bank,
			discount: 0.2,
			discountTimestampStart: Date.now() - 10_000,
			discountTimestampEnd: Date.now() + 10_000,
			itemsRarity: [TechTuningRarity.UNCOMMON, TechTuningRarity.RARE],
			inStock: 0,
		},
		[TechTuningCaseType.MAXIMUM]: {
			type: TechTuningCaseType.MAXIMUM,
			price: 300,
			walletType: WalletType.Bank,
			discount: null,
			discountTimestampStart: null,
			discountTimestampEnd: null,
			itemsRarity: [TechTuningRarity.EPIC, TechTuningRarity.LEGENDARY],
			inStock: 0,
		},
	};

	private readonly INSTALLED_ITEM_IDS_STUB = ['6'];

	private readonly OWNED_ITEMS_STUB = [
		{ id: '6', count: 2 },
		{ id: '1', count: 1 },
		{ id: '2', count: 1 },
	];

	private readonly SELL_PRICE_RATE_STUB = 0.3;

	private items = new Subject<VehicleTechTuningItemConfig[]>([]);
	private shopItems = new Subject<VehicleTechTuningItemConfig[]>([]);
	private readonly caseItems: Record<
		TechTuningCaseType,
		Subject<VehicleTechTuningItemConfig[]>
	> = Object.values(TechTuningCaseType).reduce(
		(acc, type) => {
			acc[type] = new Subject<VehicleTechTuningItemConfig[]>([]);
			return acc;
		},
		{} as Record<TechTuningCaseType, Subject<VehicleTechTuningItemConfig[]>>,
	);

	private cases = new Subject<TechTuningCaseConfigByType | null>(null);

	private installedItems = new Subject<string[]>(this.INSTALLED_ITEM_IDS_STUB);

	private ownedItems = new Subject<ITechTuningItem[]>(this.OWNED_ITEMS_STUB);

	constructor() {
		this.items.next(this.ITEMS_STUB);
		this.shopItems.next(this.ITEMS_STUB.filter((item) => item.isShopItem));

		for (const caseConfig of Object.values(this.CASES_STUB)) {
			const caseSubj = this.caseItems[caseConfig.type];
			const caseItems = this.ITEMS_STUB.filter((item) =>
				caseConfig.itemsRarity.includes(item.rarity),
			);
			caseSubj.next(caseItems);
		}

		this.cases.next(this.CASES_STUB);
	}

	public getItems(): IObservable<VehicleTechTuningItemConfig[]> {
		return this.items;
	}

	public getShopItems(): IObservable<VehicleTechTuningItemConfig[]> {
		return this.shopItems;
	}

	public getCaseItems(
		caseType: TechTuningCaseType,
	): IObservable<VehicleTechTuningItemConfig[]> {
		return this.caseItems[caseType];
	}

	public getOwnedItems(): IObservable<ITechTuningItem[]> {
		return this.ownedItems;
	}

	public async fetchInstalledItems(): Promise<void> {
		console.log('techTuning: fetchInstalledItems');
		return Promise.resolve();
	}

	public getInstalledItems(): IObservable<string[]> {
		return this.installedItems;
	}

	private getItemById(itemId: string): VehicleTechTuningItemConfig | null {
		return this.ITEMS_STUB.find((e) => e.id === itemId) ?? null;
	}

	public getItemBuyPrice(itemId: string): number {
		const itemConfig = this.getItemById(itemId);
		if (!itemConfig) {
			return 0;
		}

		const discount = itemConfig.discount ?? 0;
		return Math.floor(itemConfig.price * (1 - discount));
	}

	public getItemSellPrice(itemId: string): number {
		const itemPrice = this.getItemBuyPrice(itemId);
		return Math.floor(itemPrice * this.SELL_PRICE_RATE_STUB);
	}

	public async buyItem(itemId: string, walletType: WalletType): Promise<void> {
		console.log('techTuning: buyItem', itemId, walletType);

		const items = this.ownedItems.getValue();

		if (items.some((item) => item.id === itemId)) {
			items.find((item) => item.id === itemId)!.count++;
		} else {
			items.push({ id: itemId, count: 1 });
		}
		this.ownedItems.next(items);

		return Promise.resolve();
	}

	public async installItem(id: string): Promise<void> {
		const allItems = this.items.getValue();
		const itemToInstall = allItems.find((item) => item.id === id);

		if (!itemToInstall) {
			return Promise.resolve();
		}

		const installedItems = [...this.installedItems.getValue()];

		const categoryOccupied = installedItems.some((installedId) => {
			const installedItem = allItems.find((item) => item.id === installedId);
			return installedItem && installedItem.category === itemToInstall.category;
		});

		if (categoryOccupied) {
			console.error(
				'techTuning: installItem category is already occupied',
				itemToInstall.category,
			);
			return Promise.resolve();
		}

		installedItems.push(id);
		this.installedItems.next(installedItems);

		const ownedItems = [...this.ownedItems.getValue()];
		const ownedItem = ownedItems.find((item) => item.id === id);

		if (ownedItem) {
			if (ownedItem.count === 1) {
				const index = ownedItems.indexOf(ownedItem);
				ownedItems.splice(index, 1);
			} else {
				ownedItem.count--;
			}
			this.ownedItems.next(ownedItems);
		}

		console.log('techTuning: installItem', id);
		return Promise.resolve();
	}

	public async sellOwned(itemId: string): Promise<void> {
		console.log('techTuning: sellOwned', itemId);

		const ownedItems = [...this.ownedItems.getValue()];
		const ownedItem = ownedItems.find((item) => item.id === itemId);

		if (ownedItem) {
			if (ownedItem.count === 1) {
				const index = ownedItems.indexOf(ownedItem);
				ownedItems.splice(index, 1);
			} else {
				ownedItem.count--;
			}
			this.ownedItems.next(ownedItems);
		}

		return Promise.resolve();
	}

	public async sellEquipped(itemId: string): Promise<void> {
		console.log('techTuning: sellEquipped', itemId);

		const installedItems = [...this.installedItems.getValue()];
		const index = installedItems.indexOf(itemId);
		if (index !== -1) {
			installedItems.splice(index, 1);
			this.installedItems.next(installedItems);
		}

		return Promise.resolve();
	}

	public getCases(): IObservable<TechTuningCaseConfigByType | null> {
		return this.cases;
	}

	public async buyCase(
		caseType: TechTuningCaseType,
		count: number,
	): Promise<void> {
		console.log('techTuning: buyCase', caseType, count);

		const casesByType = _.cloneDeep(this.cases.getValue());
		if (!casesByType) {
			throw new Error(`Case config is not ready`);
		}

		casesByType[caseType].inStock += count;
		this.cases.next(casesByType);

		return Promise.resolve();
	}

	public async openCase(
		caseType: TechTuningCaseType,
	): Promise<{ itemId: string }> {
		console.log('techTuning: openCase', caseType);

		const casesByType = _.cloneDeep(this.cases.getValue());
		if (!casesByType) {
			throw new Error(`Case config is not ready`);
		}

		casesByType[caseType].inStock--;
		this.cases.next(casesByType);

		const items = [...this.getItems().getValue()];
		const rarities = casesByType[caseType].itemsRarity;

		const randomRarity = rarities[Math.floor(Math.random() * rarities.length)];
		const rarityItems = items.filter((e) => e.rarity === randomRarity);

		const randomItemId =
			rarityItems[Math.floor(Math.random() * rarityItems.length)].id;
		return Promise.resolve({
			itemId: randomItemId,
		});
	}

	public async exitMenu(): Promise<void> {
		console.log('techTuning: exit');
		return Promise.resolve();
	}
}
